import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { BsCloudUpload } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { getClientApi, saveClientApi } from "../../../../Api/ClientWork/ClientWork";
import "../../AddContractorsAndVendors.css";

const BUSINESS_TYPE_OPTIONS = [
  { value: "", label: "Select Business Type" },
  { value: "Individual", label: "Individual" },
  { value: "Sole Proprietorship", label: "Sole Proprietorship" },
  { value: "Partnership", label: "Partnership" },
  { value: "Private Limited Company", label: "Private Limited Company" },
  { value: "Government", label: "Government" },
  { value: "Public Listed Company", label: "Public Listed Company" },
];

const SALES_TAX_OPTIONS = [
  { value: "", label: "Select Sales Tax Authority" },
  { value: "PRA (Punjab Revenue Authority)", label: "PRA (Punjab Revenue Authority)" },
  { value: "SRB (Sindh Revenue Board)", label: "SRB (Sindh Revenue Board)" },
  { value: "KPRA (Khyber Pakhtunkhwa Revenue Authority)", label: "KPRA (Khyber Pakhtunkhwa)" },
  { value: "BRA (Balochistan Revenue Authority)", label: "BRA (Balochistan Revenue Authority)" },
  { value: "FBR (Federal Board of Revenue)", label: "FBR (Federal Board of Revenue)" },
  { value: "Exempt", label: "Exempt" },
];

const PROVINCE_OPTIONS = [
  { value: "", label: "Select State / Province" },
  { value: "Punjab", label: "Punjab" },
  { value: "Sindh", label: "Sindh" },
  { value: "Khyber Pakhtunkhwa", label: "Khyber Pakhtunkhwa" },
  { value: "Balochistan", label: "Balochistan" },
  { value: "Islamabad Capital Territory", label: "Islamabad Capital Territory" },
  { value: "Gilgit-Baltistan", label: "Gilgit-Baltistan" },
  { value: "Azad Jammu & Kashmir", label: "Azad Jammu & Kashmir" },
];

const CITY_OPTIONS = [
  { value: "", label: "Select City" },
  { value: "Lahore", label: "Lahore" },
  { value: "Karachi", label: "Karachi" },
  { value: "Islamabad", label: "Islamabad" },
  { value: "Rawalpindi", label: "Rawalpindi" },
  { value: "Faisalabad", label: "Faisalabad" },
  { value: "Multan", label: "Multan" },
  { value: "Peshawar", label: "Peshawar" },
  { value: "Quetta", label: "Quetta" },
  { value: "Gujranwala", label: "Gujranwala" },
  { value: "Sialkot", label: "Sialkot" },
  { value: "Hyderabad", label: "Hyderabad" },
  { value: "Bahawalpur", label: "Bahawalpur" },
  { value: "Sargodha", label: "Sargodha" },
];

const PERFORMANCE_GUARANTEE_OPTIONS = [
  { value: "No", label: "Not Required (No)" },
  { value: "Yes", label: "Required (Yes)" },
];

/**
 * ClientForm Screen (Add / Edit Client)
 * Built with standard ERP components (InputField, SelectField, TextAreaField, ActionButton, vendor-card)
 */
const ClientForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const fileInputRef = useRef(null);

  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isNtnVerified, setIsNtnVerified] = useState(false);

  const [formData, setFormData] = useState({
    OwnerName: "",
    ContactPerson: "",
    Designation: "",
    BusinessType: "Private Limited Company",
    NTN: "",
    Mobile: "",
    OfficeLandline: "",
    Email: "",
    TaxStatus: "Filer",
    SalesTaxStatus: "PRA (Punjab Revenue Authority)",
    IncomeTaxWhtRate: "2.0",
    SalesTaxWhtRate: "4.0",
    DefaultRetentionRate: "5.0",
    PerformanceGuaranteeRequire: "No",
    City: "Lahore",
    StateProvince: "Punjab",
    OfficialAddress: "",
    Status: "Active",
    Documents: [],
  });

  useEffect(() => {
    if (!isEdit) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = await getClientApi(id);
      if (cancelled) {
        return;
      }
      const match = result?.data?.IsSuccess === true ? result.data.Data : null;
      if (match) {
        setFormData({
          OwnerName: match.OwnerName || "",
          ContactPerson: match.ContactPerson || "",
          Designation: match.Designation || "",
          BusinessType: match.BusinessType || "Private Limited Company",
          NTN: match.NTN || "",
          Mobile: match.Mobile || "",
          OfficeLandline: match.OfficeLandline || "",
          Email: match.Email || "",
          TaxStatus: match.TaxStatus || "Filer",
          SalesTaxStatus: match.SalesTaxStatus || "PRA (Punjab Revenue Authority)",
          IncomeTaxWhtRate: match.IncomeTaxWhtRate != null ? String(match.IncomeTaxWhtRate) : "2.0",
          SalesTaxWhtRate: match.SalesTaxWhtRate != null ? String(match.SalesTaxWhtRate) : "4.0",
          DefaultRetentionRate:
            match.DefaultRetentionRate != null ? String(match.DefaultRetentionRate) : "5.0",
          PerformanceGuaranteeRequire: match.PerformanceGuaranteeRequire || "No",
          City: match.City || "Lahore",
          StateProvince: match.StateProvince || "Punjab",
          OfficialAddress: match.OfficialAddress || "",
          Status: match.Status || "Active",
          Documents: Array.isArray(match.Documents) ? match.Documents : [],
        });
        if (match.NTN) {
          setIsNtnVerified(true);
        }
      } else {
        toast.error("Client record not found");
        history.push("/client-work/clients");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, history]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "NTN") {
      setIsNtnVerified(false);
    }
  };

  const handleVerifyNTN = () => {
    if (!formData.NTN || formData.NTN.trim().length < 5) {
      toast.warning("Please enter a valid NTN number to verify");
      return;
    }
    setIsNtnVerified(true);
    toast.success(`NTN ${formData.NTN} verified with Active Taxpayer Registry`);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      return;
    }
    const newDocs = files.map((file) => ({
      name: file.name,
      size: `${(file.size / 1024).toFixed(0)} KB`,
    }));
    setFormData((prev) => ({
      ...prev,
      Documents: [...prev.Documents, ...newDocs],
    }));
    toast.success(`${files.length} document(s) uploaded`);
  };

  const handleRemoveDoc = (index) => {
    setFormData((prev) => ({
      ...prev,
      Documents: prev.Documents.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!formData.OwnerName.trim()) {
      toast.error("Owner / Company Name is required");
      return;
    }
    if (!formData.ContactPerson.trim()) {
      toast.error("Contact Person Name is required");
      return;
    }
    if (!formData.Mobile.trim()) {
      toast.error("Owner Mobile / WhatsApp is required");
      return;
    }
    if (!formData.Email.trim()) {
      toast.error("Email Address is required");
      return;
    }
    if (!formData.City) {
      toast.error("City is required");
      return;
    }
    if (!formData.OfficialAddress.trim()) {
      toast.error("Official Business Address is required");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        Id: isEdit ? Number(id) : null,
        ...formData,
        OwnerName: formData.OwnerName.trim(),
        ContactPerson: formData.ContactPerson.trim(),
        Email: formData.Email.trim(),
        NTN: formData.NTN.trim(),
        Mobile: formData.Mobile.trim(),
        OfficialAddress: formData.OfficialAddress.trim(),
      };

      const result = await saveClientApi(payload);
      if (result?.data?.IsSuccess === true) {
        toast.success(isEdit ? "Client updated successfully" : "Client saved successfully");
        history.push("/client-work/clients");
      } else {
        toast.error(result?.data?.Message || "Failed to save Client");
      }
    } catch (err) {
      toast.error("Failed to save Client");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="vendor-screen-wrapper app_font">
      {/* ── Page Header ── */}
      <div className="col-md-12 text-left page_heading">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back"
            onClick={() => history.push("/client-work/clients")}
          />
          <h4 className="m-0">{isEdit ? "Edit" : "Add"} Client</h4>
        </div>
      </div>

      <form onSubmit={handleSubmit} autoComplete="off">
        {/* ── CARD 1: Client & Identity Details ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Client & Identity Details</h5>

          <div className="row mb-2">
            <InputField
              label="Owner / Company Name:"
              name="OwnerName"
              value={formData.OwnerName}
              onChange={handleChange}
              placeholder="e.g. Amanat Builders & Developers (Pvt) Ltd"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Contact Person Name:"
              name="ContactPerson"
              value={formData.ContactPerson}
              onChange={handleChange}
              placeholder="e.g. Sheikh Amanat Ali"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Designation:"
              name="Designation"
              value={formData.Designation}
              onChange={handleChange}
              placeholder="e.g. Chief Executive Officer"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <SelectField
              label="Business Type:"
              name="BusinessType"
              value={formData.BusinessType}
              onChange={handleChange}
              options={BUSINESS_TYPE_OPTIONS}
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Mobile / WhatsApp:"
              name="Mobile"
              value={formData.Mobile}
              onChange={handleChange}
              placeholder="0300-8451222"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Office Landline:"
              name="OfficeLandline"
              value={formData.OfficeLandline}
              onChange={handleChange}
              placeholder="042-35711222"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Email Address:"
              name="Email"
              type="email"
              value={formData.Email}
              onChange={handleChange}
              placeholder="contact@amanatbuilders.com"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
          </div>
        </div>

        {/* ── CARD 2: Commercial & Tax Terms ── */}
        <div className="vendor-card">
          <div className="vendor-card-header">
            <h5 className="vendor-card-title">Commercial & Tax Terms</h5>

            <div className="vendor-tax-pill-group">
              <button
                type="button"
                className={`vendor-tax-pill ${formData.TaxStatus === "Filer" ? "active" : ""}`}
                onClick={() => setFormData((prev) => ({ ...prev, TaxStatus: "Filer" }))}
              >
                Filer
              </button>

              <button
                type="button"
                className={`vendor-tax-pill ${formData.TaxStatus === "Non-Filer" ? "active" : ""}`}
                onClick={() => setFormData((prev) => ({ ...prev, TaxStatus: "Non-Filer" }))}
              >
                Non-Filer
              </button>
            </div>
          </div>

          <div className="row mb-2">
            {formData.TaxStatus !== "Non-Filer" && (
              <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
                <label className="input_label d-block m-0 mb-2">NTN Number:</label>
                <div className="position-relative">
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      name="NTN"
                      className="input_styling"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        width: "auto",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                      value={formData.NTN}
                      onChange={handleChange}
                      placeholder="4298765-4"
                    />
                    <button
                      type="button"
                      className={`btn btn-sm ${isNtnVerified ? "btn-info" : "btn-outline-info"}`}
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: "none",
                        height: "32px",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        padding: "0 10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={handleVerifyNTN}
                    >
                      {isNtnVerified ? "✓ Verified" : "Verify NTN"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <SelectField
              label="Sales Tax Authority:"
              name="SalesTaxStatus"
              value={formData.SalesTaxStatus}
              onChange={handleChange}
              options={SALES_TAX_OPTIONS}
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Income Tax WHT Rate (%):"
              name="IncomeTaxWhtRate"
              type="number"
              step="0.1"
              value={formData.IncomeTaxWhtRate}
              onChange={handleChange}
              placeholder="2.0"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Sales Tax WHT Rate (%):"
              name="SalesTaxWhtRate"
              type="number"
              step="0.1"
              value={formData.SalesTaxWhtRate}
              onChange={handleChange}
              placeholder="4.0"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Default Retention Rate (%):"
              name="DefaultRetentionRate"
              type="number"
              step="0.1"
              value={formData.DefaultRetentionRate}
              onChange={handleChange}
              placeholder="5.0"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <SelectField
              label="Performance Guarantee:"
              name="PerformanceGuaranteeRequire"
              value={formData.PerformanceGuaranteeRequire}
              onChange={handleChange}
              options={PERFORMANCE_GUARANTEE_OPTIONS}
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
          </div>
        </div>

        {/* ── CARD 3: Address & Documents ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Address & Attachments</h5>

          <div className="row mb-2">
            <SelectField
              label="City:"
              name="City"
              value={formData.City}
              onChange={handleChange}
              options={CITY_OPTIONS}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <SelectField
              label="State / Province:"
              name="StateProvince"
              value={formData.StateProvince}
              onChange={handleChange}
              options={PROVINCE_OPTIONS}
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <TextAreaField
              label="Official Business Address:"
              name="OfficialAddress"
              value={formData.OfficialAddress}
              onChange={handleChange}
              placeholder="Plot 45, Commercial Plaza, Main Boulevard..."
              rows={2}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
              <label className="input_label d-block m-0 mb-2">KYC Attachments:</label>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                hidden
                onChange={handleFileUpload}
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              />
              <div
                className="document-upload-box"
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <BsCloudUpload className="document-upload-icon" />
                <div className="document-upload-copy">
                  <strong>
                    {formData.Documents.length ? "Add more documents" : "Upload KYC documents"}
                  </strong>
                  <span>PDF, images or Word files</span>
                </div>
              </div>

              {formData.Documents.length > 0 && (
                <div className="document-upload-list mt-2">
                  {formData.Documents.map((doc, idx) => (
                    <div key={`${doc.name}-${idx}`} className="document-upload-file-item">
                      <span className="text-truncate" title={doc.name}>
                        {doc.name}
                      </span>
                      <small>{doc.size}</small>
                      <button
                        type="button"
                        className="document-upload-remove"
                        onClick={() => handleRemoveDoc(idx)}
                        title="Remove document"
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom Action Buttons ── */}
        <div className="col-12 p-0">
          <div className="float-right mt-2 mb-4 d-flex align-items-center" style={{ gap: "10px" }}>
            <ActionButton
              text="Cancel"
              variant="cancel"
              type="button"
              onClick={() => history.push("/client-work/clients")}
              disabled={isLoading}
            />
            <ActionButton
              text={`${isEdit ? "Update" : "Save"} Client`}
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
